import { Button } from "@d12labs/ui/button";
import { Card } from "@d12labs/ui/card";
import { formatClock } from "@d12labs/utils";
import { useEffect, useRef, useState } from "react";
import { RecordingWaveform } from "./recording-waveform";

// ─── Types ────────────────────────────────────────────────────────────────────

type RecorderState =
	| { kind: "idle" }
	| { kind: "requesting-permission" }
	| { kind: "permission-denied"; message: string }
	| { kind: "recording"; startedAt: number; analyser: AnalyserNode }
	| { kind: "recorded"; blob: Blob; durationSec: number; url: string }
	| { kind: "submitting"; blob: Blob; durationSec: number; url: string }
	| { kind: "submit-error"; blob: Blob; durationSec: number; url: string; message: string };

// ─── Helpers ──────────────────────────────────────────────────────────────────

function pickMimeType(): string {
	// audio/webm;codecs=opus works on Chrome/Edge/Firefox.
	// Safari does not support webm — fall back to audio/mp4 if needed.
	if (MediaRecorder.isTypeSupported("audio/webm;codecs=opus")) {
		return "audio/webm;codecs=opus";
	}
	if (MediaRecorder.isTypeSupported("audio/mp4")) {
		return "audio/mp4";
	}
	return ""; // browser default
}

// ─── Component ────────────────────────────────────────────────────────────────

export function Recorder({
	onSubmit,
	disabled,
}: {
	onSubmit: (blob: Blob, durationSec: number) => Promise<void>;
	disabled?: boolean;
}) {
	const [state, setState] = useState<RecorderState>({ kind: "idle" });
	const [elapsedMs, setElapsedMs] = useState(0);

	// Refs for MediaRecorder teardown
	const recorderRef = useRef<MediaRecorder | null>(null);
	const streamRef = useRef<MediaStream | null>(null);
	const audioCtxRef = useRef<AudioContext | null>(null);
	const chunksRef = useRef<Blob[]>([]);
	const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
	const objectUrlRef = useRef<string | null>(null);

	// Cleanup on unmount: stop tracks, close AudioContext, revoke URLs, clear timer
	useEffect(() => {
		return () => {
			if (timerRef.current != null) clearInterval(timerRef.current);
			if (streamRef.current) {
				for (const track of streamRef.current.getTracks()) track.stop();
			}
			if (audioCtxRef.current && audioCtxRef.current.state !== "closed") {
				audioCtxRef.current.close();
			}
			if (objectUrlRef.current) {
				URL.revokeObjectURL(objectUrlRef.current);
			}
		};
	}, []);

	// ── Handlers ────────────────────────────────────────────────────────────────

	async function handleStart() {
		setState({ kind: "requesting-permission" });
		try {
			const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
			streamRef.current = stream;

			// AudioContext must be created inside a user gesture (autoplay policy).
			const audioCtx = new AudioContext();
			audioCtxRef.current = audioCtx;
			const source = audioCtx.createMediaStreamSource(stream);
			const analyser = audioCtx.createAnalyser();
			analyser.fftSize = 256;
			// Connect source → analyser only (NOT to destination — we don't want mic playback).
			source.connect(analyser);

			chunksRef.current = [];
			const mimeType = pickMimeType();
			const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
			recorderRef.current = recorder;

			recorder.ondataavailable = (e) => {
				if (e.data.size > 0) chunksRef.current.push(e.data);
			};

			recorder.onstop = () => {
				const blob = new Blob(chunksRef.current, {
					type: mimeType || "audio/webm",
				});
				const url = URL.createObjectURL(blob);
				objectUrlRef.current = url;
				const durationSec = (Date.now() - startedAt) / 1000;

				// Stop mic indicator
				for (const track of stream.getTracks()) track.stop();
				if (audioCtx.state !== "closed") audioCtx.close();
				if (timerRef.current != null) clearInterval(timerRef.current);

				setState({ kind: "recorded", blob, durationSec, url });
			};

			const startedAt = Date.now();
			recorder.start();

			setElapsedMs(0);
			timerRef.current = setInterval(() => {
				setElapsedMs(Date.now() - startedAt);
			}, 250);

			setState({ kind: "recording", startedAt, analyser });
		} catch (err) {
			const e = err as Error;
			let message: string;
			if (e.name === "NotAllowedError") {
				message = "Microphone access denied. Allow it in your browser settings to record.";
			} else if (e.name === "NotFoundError") {
				message = "No microphone detected.";
			} else {
				message = e.message;
			}
			setState({ kind: "permission-denied", message });
		}
	}

	function handleStop() {
		if (recorderRef.current && recorderRef.current.state !== "inactive") {
			recorderRef.current.stop();
		}
	}

	function handleReRecord() {
		if (objectUrlRef.current) {
			URL.revokeObjectURL(objectUrlRef.current);
			objectUrlRef.current = null;
		}
		setState({ kind: "idle" });
	}

	function handleDiscard() {
		if (objectUrlRef.current) {
			URL.revokeObjectURL(objectUrlRef.current);
			objectUrlRef.current = null;
		}
		setState({ kind: "idle" });
	}

	async function handleSubmit(blob: Blob, durationSec: number, url: string) {
		setState({ kind: "submitting", blob, durationSec, url });
		try {
			await onSubmit(blob, durationSec);
			// onSubmit's caller handles next state (e.g. may unmount this component).
		} catch (err) {
			const message = err instanceof Error ? err.message : "Submission failed";
			setState({ kind: "submit-error", blob, durationSec, url, message });
		}
	}

	async function handleRetry(blob: Blob, durationSec: number, url: string) {
		await handleSubmit(blob, durationSec, url);
	}

	// ── Render ──────────────────────────────────────────────────────────────────

	return (
		<Card className="p-4 space-y-3 min-h-[180px]">
			{state.kind === "idle" && (
				<div className="flex flex-col gap-3">
					<p className="text-sm text-ink-subtle">
						Click Start to begin recording. Your browser will ask for microphone access.
					</p>
					<div>
						<Button onClick={handleStart} disabled={disabled}>
							Start recording
						</Button>
					</div>
				</div>
			)}

			{state.kind === "requesting-permission" && (
				<p className="text-sm text-ink-subtle">Requesting microphone access…</p>
			)}

			{state.kind === "permission-denied" && (
				<div className="flex flex-col gap-3">
					<p className="text-sm text-destructive">{state.message}</p>
					<div>
						<Button variant="outline" onClick={handleStart}>
							Try again
						</Button>
					</div>
				</div>
			)}

			{state.kind === "recording" && (
				<div className="flex flex-col gap-3">
					<div className="flex items-center justify-between">
						<span className="text-2xl tabular-nums font-semibold text-foreground">
							{formatClock(elapsedMs / 1000)}
						</span>
						<span className="text-xs text-ink-subtle">Recording…</span>
					</div>
					<RecordingWaveform analyser={state.analyser} active={true} />
					<div>
						<Button variant="destructive" onClick={handleStop}>
							Stop
						</Button>
					</div>
				</div>
			)}

			{state.kind === "recorded" && (
				<div className="flex flex-col gap-3">
					{/* biome-ignore lint/a11y/useMediaCaption: user-generated content, no captions available */}
					<audio controls src={state.url} className="w-full" />
					<RecordingWaveform analyser={null} active={false} />
					<div className="flex items-center gap-2">
						<Button onClick={() => handleSubmit(state.blob, state.durationSec, state.url)}>
							Submit
						</Button>
						<Button variant="outline" onClick={handleReRecord}>
							Re-record
						</Button>
						<Button variant="ghost" onClick={handleDiscard}>
							Discard
						</Button>
					</div>
				</div>
			)}

			{state.kind === "submitting" && (
				<div className="flex flex-col gap-3">
					{/* biome-ignore lint/a11y/useMediaCaption: user-generated content, no captions available */}
					<audio controls src={state.url} className="w-full" />
					<p className="text-sm text-ink-subtle">Submitting…</p>
				</div>
			)}

			{state.kind === "submit-error" && (
				<div className="flex flex-col gap-3">
					{/* biome-ignore lint/a11y/useMediaCaption: user-generated content, no captions available */}
					<audio controls src={state.url} className="w-full" />
					<p className="text-sm text-destructive">{state.message}</p>
					<div className="flex items-center gap-2">
						<Button onClick={() => handleRetry(state.blob, state.durationSec, state.url)}>
							Retry
						</Button>
						<Button variant="outline" onClick={handleReRecord}>
							Re-record
						</Button>
						<Button variant="ghost" onClick={handleDiscard}>
							Discard
						</Button>
					</div>
				</div>
			)}
		</Card>
	);
}
